
import { useState } from 'react';
import { toast } from 'sonner';
import { openAiService } from '@/services/ai';

export const useLeadGenerator = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeFeature, setActiveFeature] = useState<string | undefined>(undefined);

  const generateLeadParagraph = async (content: string) => {
    try {
      // Enhance content validation
      if (!content || content.trim().length === 0) {
        toast.error("Cannot generate lead paragraphs for empty content. Please add some text to your article first.");
        return { 
          success: false, 
          error: "Cannot generate leads for empty content" 
        };
      }

      // Ensure we're sending the full content, not just a sample
      const fullContent = content.length > 0 ? content : '';
      
      setIsProcessing(true);
      setActiveFeature('generateLead');
      
      console.log("Generating lead paragraph for full content length:", fullContent.length);

      const response = await openAiService.generateLeadParagraph(fullContent);

      setIsProcessing(false);
      setActiveFeature(undefined);

      if (!response.success) {
        console.error("Error generating lead paragraph:", response.error);
        toast.error(`Failed to generate lead paragraph: ${response.error || "Unknown error"}`);
        return response;
      }

      if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
        console.error("Empty response from AI service");
        toast.error("Received empty response from AI service. Please try again.");
        return { 
          success: false, 
          error: "Empty response from AI service" 
        };
      }

      console.log("Lead paragraph generation successful, response:", response.data);
      return response;
    } catch (error) {
      setIsProcessing(false);
      setActiveFeature(undefined);
      console.error("Error generating lead paragraph:", error);
      toast.error("Failed to generate lead paragraph. Please try again.");
      return { 
        success: false, 
        error: error instanceof Error ? error.message : "Unknown error" 
      };
    }
  };

  const setLead = async (articleId: string, lead: string) => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .update({ lead })
        .eq('id', articleId)
        .single();
        
      if (error) {
        console.error("Error setting lead paragraph:", error);
        toast.error("Failed to save lead paragraph to database");
        return false;
      }
      
      toast.success("Lead paragraph saved successfully");
      return true;
    } catch (error) {
      console.error("Error setting lead paragraph:", error);
      toast.error("Failed to save lead paragraph");
      return false;
    }
  };

  return {
    generateLeadParagraph,
    setLead,
    isProcessing,
    activeFeature
  };
};

import { supabase } from '@/integrations/supabase/client';
